import { z } from "zod";
import type { supportMessageAuthorValues } from "./enums";

// Support: a customer's staff member talking to the Vidimo team (ADR-0136).

/**
 * Where a support conversation stands for the support team. A customer message
 * always puts it back to `open`; an agent's reply moves it to
 * `waiting_on_customer`; `resolved` is set by an agent or by the silence sweep.
 */
export const supportConversationStatusValues = [
  "open",
  "waiting_on_customer",
  "resolved",
] as const;
export type SupportConversationStatus =
  (typeof supportConversationStatusValues)[number];

/**
 * The `?id=` the Chats tab opens the Vidimo team conversation under. Bare, it
 * means the user's own conversation for the property they are working in;
 * followed by a conversation id, that conversation, which is what a reply's
 * push and email carry and may belong to another property.
 */
export const SUPPORT_CHAT_ID = "vidimo-team";
const SUPPORT_CHAT_ID_SEPARATOR = ":";

export function supportChatId(conversationId?: string): string {
  return conversationId
    ? `${SUPPORT_CHAT_ID}${SUPPORT_CHAT_ID_SEPARATOR}${conversationId}`
    : SUPPORT_CHAT_ID;
}

/**
 * Reads a Chats `?id=` as a support chat: the conversation it names (null for
 * the bare id), or undefined when the id is some other chat's.
 */
export function parseSupportChatId(
  chatId: string
): { conversationId: string | null } | undefined {
  if (chatId === SUPPORT_CHAT_ID) {
    return { conversationId: null };
  }
  const prefix = `${SUPPORT_CHAT_ID}${SUPPORT_CHAT_ID_SEPARATOR}`;
  return chatId.startsWith(prefix)
    ? { conversationId: chatId.slice(prefix.length) }
    : undefined;
}

/**
 * The language the support team reads and writes. A customer message in any
 * other language is translated into it for the CRM, and a reply written in it
 * is translated into the customer's.
 */
export const SUPPORT_TEAM_LANGUAGE = "en";

/** What a customer message is about; a message can carry several. */
export const supportMessageTagValues = [
  "bug",
  "feedback",
  "question",
  "compliance",
  "billing",
] as const;
export type SupportMessageTag = (typeof supportMessageTagValues)[number];

/** The part of the product a customer message is about. */
export const supportFeatureAreaValues = [
  "filing",
  "tax",
  "calendar",
  "reservations",
  "guests",
  "messaging",
  "channels",
  "tasks",
  "payments",
  "team",
  "account",
  "other",
] as const;
export type SupportFeatureArea = (typeof supportFeatureAreaValues)[number];

/** How soon a customer message suggests it needs an answer. */
export const supportUrgencyValues = [
  "low",
  "normal",
  "high",
  "urgent",
] as const;
export type SupportUrgency = (typeof supportUrgencyValues)[number];

/**
 * The text a customer reads for a support message. A reply carries at most one
 * translation, into the customer's language, and they read that; everything
 * else they read as written.
 */
export function supportMessageText(message: {
  author: (typeof supportMessageAuthorValues)[number];
  content: string;
  translations: Record<string, string>;
}): { original: string | null; text: string } {
  const [translation] = Object.values(message.translations);
  return message.author === "agent" && translation
    ? { original: message.content, text: translation }
    : { original: null, text: message.content };
}

// Support sessions: an agent opening the app as one of a customer's staff
// (ADR-0143). The CRM mints a one-time code and opens the app's web build at
// `SUPPORT_SESSION_PATH`, the code in the URL fragment, which no server or
// referrer ever receives; the app trades it for the session.

/** How long a code from the CRM can still be traded for a session. */
export const SUPPORT_SESSION_CODE_TTL_SECONDS = 60;
/** The longest a support session is accepted for, from the moment it opens. */
export const SUPPORT_SESSION_MAX_SECONDS = 3600;
export const SUPPORT_SESSION_PATH = "/support-session";
export const SUPPORT_SESSION_CODE_PARAM = "code";
/** 32 random bytes, base64url: 43 characters. */
export const SUPPORT_SESSION_CODE_LENGTH = 43;
/** The error code better-auth answers with for an endpoint a support session may not call. */
export const SUPPORT_SESSION_REFUSED_CODE = "NOT_IN_SUPPORT_SESSION";

export const StartSupportSessionSchema = z.object({
  code: z.string().length(SUPPORT_SESSION_CODE_LENGTH),
});

/** The session a code was traded for, held by the tab that traded it. */
export const SupportSessionGrantSchema = z.object({
  expiresAt: z.iso.datetime(),
  token: z.string().min(1),
});
export type SupportSessionGrant = z.infer<typeof SupportSessionGrantSchema>;

export const SUPPORT_MESSAGE_MAX_LENGTH = 5000;
const CONTEXT_TEXT_MAX_LENGTH = 300;

// A field the app got wrong is dropped, never refused: the context is there
// to help the support team, and must not be what stops a message reaching it.
const contextText = z
  .string()
  .trim()
  .max(CONTEXT_TEXT_MAX_LENGTH)
  .optional()
  .catch(undefined);

/**
 * What the app knows at the moment a message is sent. Every field is advisory:
 * the server reads the property, the sender and their role from the session,
 * and looks the named reservation up inside the sender's own property.
 */
export const SupportClientContextSchema = z.object({
  appVersion: contextText,
  commit: contextText,
  /** The interface language, as a BCP-47 tag. */
  language: contextText,
  platform: contextText,
  /** The reservation open on `screen`, when there is one. */
  reservationId: z.uuid().optional().catch(undefined),
  /** Path of the last screen outside the Chats tab, without its query. */
  screen: contextText,
  /** The device's IANA zone, for "when we're back" in the customer's time. */
  timeZone: contextText,
});
export type SupportClientContext = z.infer<typeof SupportClientContextSchema>;

/** Photos one support message can carry. */
export const SUPPORT_PHOTOS_MAX = 4;

/**
 * A photo the app uploaded under the "support" scope, still in staging: the
 * send verifies and keeps it, so a photo whose message never goes is reaped
 * with the rest of staging.
 */
export const SupportPhotoSchema = z.object({ stagingKey: z.string().min(1) });
export type SupportPhoto = z.infer<typeof SupportPhotoSchema>;

export const SendSupportMessageSchema = z
  .object({
    attachments: z
      .array(SupportPhotoSchema)
      .max(SUPPORT_PHOTOS_MAX)
      .default([]),
    content: z.string().trim().max(SUPPORT_MESSAGE_MAX_LENGTH),
    context: SupportClientContextSchema.catch({}).default({}),
    /** Minted on the device, so a retried send cannot post the message twice. */
    id: z.uuid(),
    propertyId: z.uuid(),
  })
  .refine((input) => input.content.length > 0 || input.attachments.length > 0, {
    message: "A message needs text or a photo",
    path: ["content"],
  });
export type SendSupportMessageInput = z.infer<typeof SendSupportMessageSchema>;

/**
 * The snapshot stored on a customer message: the app's own context plus what
 * only the server knows. Kept as it was when the message was sent.
 */
export interface SupportMessageContext extends SupportClientContext {
  filing?: {
    lastErrorAt: string | null;
    lastErrorCode: string | null;
    lastErrorMessage: string | null;
    status: string;
    system: string;
  } | null;
  propertyName?: string | null;
  registrationSystem?: string | null;
  reservation?: {
    checkIn: string | null;
    checkOut: string | null;
    guestName: string | null;
    id: string;
  } | null;
  senderName?: string | null;
  senderRole?: string | null;
}

// Office hours, in the support team's own zone.
const SUPPORT_OFFICE_TIME_ZONE = "Europe/Ljubljana";
const SUPPORT_OFFICE_OPENS_AT_HOUR = 9;
const SUPPORT_OFFICE_CLOSES_AT_HOUR = 17;
const OFFICE_WEEKDAYS = new Set(["Mon", "Tue", "Wed", "Thu", "Fri"]);

const HALF_HOUR_MS = 30 * 60 * 1000;
const FALLBACK_TIME_ZONE = "UTC";

function officeClock(instant: Date): { hour: number; weekday: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    hourCycle: "h23",
    timeZone: SUPPORT_OFFICE_TIME_ZONE,
    weekday: "short",
  }).formatToParts(instant);
  const read = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return { hour: Number(read("hour")), weekday: read("weekday") };
}

export function isSupportOfficeOpen(instant: Date): boolean {
  const { hour, weekday } = officeClock(instant);
  return (
    OFFICE_WEEKDAYS.has(weekday) &&
    hour >= SUPPORT_OFFICE_OPENS_AT_HOUR &&
    hour < SUPPORT_OFFICE_CLOSES_AT_HOUR
  );
}

/**
 * The next instant the support team is at work, or `instant` itself when they
 * already are. Walks half-hour boundaries, which every office-zone opening
 * time falls on, so it needs no zone arithmetic of its own.
 */
export function nextSupportOfficeOpening(instant: Date): Date {
  if (isSupportOfficeOpen(instant)) {
    return instant;
  }
  let candidate = Math.ceil(instant.getTime() / HALF_HOUR_MS) * HALF_HOUR_MS;
  while (!isSupportOfficeOpen(new Date(candidate))) {
    candidate += HALF_HOUR_MS;
  }
  return new Date(candidate);
}

function formatOpening(opening: Date, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    weekday: "long",
  };
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat("en-GB", { ...options, timeZone });
  } catch {
    formatter = new Intl.DateTimeFormat("en-GB", {
      ...options,
      timeZone: FALLBACK_TIME_ZONE,
    });
  }
  const parts = formatter.formatToParts(opening);
  const read = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${read("weekday")} at ${read("hour")}:${read("minute")}`;
}

/**
 * The sentence that tells a customer when to expect an answer, for the
 * confirmation that follows every message they send.
 */
export function supportReplyExpectation(
  sentAt: Date,
  customerTimeZone: string | null | undefined
): string {
  if (isSupportOfficeOpen(sentAt)) {
    return "We usually reply within a few hours. You'll be notified here and by email.";
  }
  const opening = formatOpening(
    nextSupportOfficeOpening(sentAt),
    customerTimeZone || FALLBACK_TIME_ZONE
  );
  return `We're away right now and back on ${opening}, your time. You'll be notified here and by email when we reply.`;
}

/**
 * The fixed confirmation posted after a customer message. Not a reply: it says
 * the message arrived, what was attached to it, and when to expect an answer.
 */
export function supportConfirmationText(
  attached: readonly string[],
  sentAt: Date,
  customerTimeZone: string | null | undefined
): string {
  const lines = ["Sent to the Vidimo team."];
  if (attached.length > 0) {
    lines.push(`We attached: ${attached.join(", ")}.`);
  }
  lines.push(supportReplyExpectation(sentAt, customerTimeZone));
  return lines.join(" ");
}

/** Posted by the silence sweep as it resolves a conversation. */
export const SUPPORT_CHECK_IN_TEXT =
  "We haven't heard back from you, so we hope that sorted it out and we're closing this for now. If you still need a hand, reply here and we'll pick it up again.";

/** How an Inbox row stands from the support team's side. */
export type SupportAttention = "unread" | "read_not_replied" | "replied";

/**
 * Whether the customer's latest message has been answered, opened, or neither.
 * A conversation nobody has written in yet counts as replied: nothing waits.
 */
export function supportAttention(marks: {
  agentReadAt: Date | string | null;
  lastAgentMessageAt: Date | string | null;
  lastCustomerMessageAt: Date | string | null;
}): SupportAttention {
  const at = (value: Date | string | null) =>
    value === null ? null : new Date(value).getTime();
  const customer = at(marks.lastCustomerMessageAt);
  if (customer === null) {
    return "replied";
  }
  const agent = at(marks.lastAgentMessageAt);
  if (agent !== null && agent >= customer) {
    return "replied";
  }
  const read = at(marks.agentReadAt);
  return read !== null && read >= customer ? "read_not_replied" : "unread";
}
